import { Config } from "@greeter/config";
import { Log } from "@greeter/log";
import { IAuthentication } from "@greeter/auth";
import * as z from "zod";

export const EmptyBody = undefined;

export function withApiVersion(version: ApiVersions) {
  return (headers: Headers) => {
    headers.set("X-Api-Version", version as string);
  };
}

type HeadersVisitorFn = (init: Headers) => void;

async function assertOk(response: Response) {
  if (response.ok) return;

  let body = "";
  try {
    body = await response.clone().json();
  } catch (error) {
    body = await response.clone().text();
  }

  if (body === "") {
    throw new ApiError(BadRequestProblemDetails());
  }

  const problemDetails = ProblemDetailsSchema.safeParse(body);

  if (problemDetails.error) {
    throw new ApiError(BadRequestProblemDetails(body));
  }

  throw new ApiError(problemDetails.data);
}

function parseContentDisposition(contentDisposition?: string | null) {
  if (!contentDisposition) return [];

  const disp = contentDisposition
    .split(";")
    .map((s) => s.trim())
    .map((kw: string) => {
      const [k, v] = kw.split("=");
      return [k, v];
    });

  return disp;
}

export const ProblemDetailsSchema = z.object({
  title: z.string(),
  status: z.number(),
  detail: z.string(),
  data: z.object({
    errorCode: z.string(),
    reasons: z.array(z.string()),
  }),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export function unwrapErrorCode(err: Error) {
  if (err instanceof ApiError) {
    return err.details.data.errorCode;
  }
  return "Ukendt fejlkode";
}

export function unwrapReasons(err: Error) {
  if (err instanceof ApiError) {
    return err.details.data.reasons;
  }
  return ["Ukendt årsag"];
}

/**
 * Used for easily testing and quickly returning error details.
 */
export function NotFoundProblemDetails(m?: string): ProblemDetails {
  return {
    title: "Entity not found",
    status: 404,
    detail: "Could not find entity",
    data: {
      errorCode: "NOT_FOUND",
      reasons: ["The entity does not exist", m ?? ""],
    },
  };
}

export function BadRequestProblemDetails(m?: string): ProblemDetails {
  return {
    title: "Bad request",
    status: 400,
    detail: "The client sent a bad request",
    data: {
      errorCode: "BAD_REQUEST",
      reasons: ["The client send a bad request", m ?? ""],
    },
  };
}

export class ApiError extends Error {
  details: ProblemDetails;

  constructor(error: ProblemDetails) {
    super();
    this.details = error;
  }

  static isNotFoundError(error?: Error) {
    if (error instanceof ApiError) {
      return error.details.status === 404;
    }
    return false;
  }

  static isBadRequestError(error?: Error) {
    if (error instanceof ApiError) {
      return error.details.status === 400;
    }
    return false;
  }
}

type RequestOptions = {
  authorize?: boolean;
  ignoreResult?: boolean;
  headersVisitor?: HeadersVisitorFn;
  abortController?: AbortController;
};

export type PostArgs = RequestOptions & {
  headers?: HeadersInit;
  body?: BodyInit;
};

type PatchOptions = RequestOptions;

class TimeoutController {
  abortController: AbortController;

  constructor(private timeoutMs: number, existing?: AbortController) {
    this.abortController = existing ?? new AbortController();
  }

  start() {
    setTimeout(() => this.abortController.abort("Timed out"), this.timeoutMs);
    return this;
  }

  get signal() {
    return this.abortController.signal;
  }
}

export enum ApiVersions {
  None,
  V1 = "v1",
  V2 = "v2",
}

export abstract class BaseProxy {
  config: Config;
  authentication: IAuthentication;
  timeoutMs = 20_000;
  apiVersion = ApiVersions.V2;

  constructor(config: Config, authentication: IAuthentication) {
    this.config = config;
    this.authentication = authentication;
  }

  protected getBaseUrl() {
    return this.config.apiUrl;
  }

  protected abstract getUrl(): URL;

  protected async post(
    url: string,
    body?: BodyInit,
    opts?: RequestOptions
  ): Promise<Response> {
    const headers = opts?.authorize
      ? await this.createHeadersWithAuth()
      : this.createHeaders();
    if (opts?.headersVisitor) opts.headersVisitor(headers);
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: body,
      signal: new TimeoutController(
        this.timeoutMs,
        opts?.abortController
      ).start().signal,
    });
    if (!opts?.ignoreResult) await assertOk(response);
    return response;
  }

  protected async getFileWithAuth(
    url: string,
    config?: (init: RequestInit) => RequestInit
  ): Promise<any> {
    const headers = await this.createHeadersWithAuth();
    const init: RequestInit = {
      method: "GET",
      headers: headers,
    };

    if (config) config(init);

    const response = await fetch(url, {
      ...init,
      signal: new TimeoutController(this.timeoutMs).start().signal,
    });

    const contentDisposition = parseContentDisposition(
      response.headers.get("Content-Disposition")
    );

    const [_, name] = contentDisposition.find(([k, v]) => {
      return k === "filename";
    }) ?? ["filename", "file.pdf"];

    const type = response.headers.get("Content-Type") ?? "application/pdf";

    return new File([await response.blob()], name, {
      type: type,
    });
  }

  protected async get(url: string, opts?: RequestOptions): Promise<Response> {
    const headers = opts?.authorize
      ? await this.createHeadersWithAuth()
      : this.createHeaders();
    if (opts?.headersVisitor) opts.headersVisitor(headers);
    const response = await fetch(url, {
      method: "GET",
      headers,
      signal: new TimeoutController(
        this.timeoutMs,
        opts?.abortController
      ).start().signal,
    });
    await assertOk(response);
    return response;
  }

  protected async delete(
    url: string,
    body?: BodyInit,
    opts?: RequestOptions
  ): Promise<Response> {
    const headers = opts?.authorize
      ? await this.createHeadersWithAuth()
      : this.createHeaders();
    if (opts?.headersVisitor) opts.headersVisitor(headers);

    const response = await fetch(url, {
      method: "DELETE",
      headers: headers,
      signal: new TimeoutController(
        this.timeoutMs,
        opts?.abortController
      ).start().signal,
      body,
    });
    return response;
  }

  protected async patch(
    url: string,
    body?: BodyInit,
    opts?: PatchOptions
  ): Promise<Response> {
    const headers = opts?.authorize
      ? await this.createHeadersWithAuth()
      : this.createHeaders();
    if (opts?.headersVisitor) opts.headersVisitor(headers);

    const response = await fetch(url, {
      method: "PATCH",
      headers: headers,
      signal: new TimeoutController(
        this.timeoutMs,
        opts?.abortController
      ).start().signal,
      body: body,
    });
    return response;
  }

  protected async createHeadersWithAuth(): Promise<Headers> {
    return await this.addAuth(this.createHeaders());
  }

  protected createHeaders(): Headers {
    const headers = new Headers();
    headers.set("Access-Control-Allow-Origin", "*");
    headers.set("Content-Type", "application/json");
    headers.set("X-Api-Version", ApiVersions.V2);
    // TODO: Should be removed in the future and default
    if (this.apiVersion !== ApiVersions.None) {
      headers.set("X-Api-Version", this.apiVersion);
    }
    return headers;
  }

  private async addAuth(headers: Headers) {
    try {
      const token = await this.authentication.getAuthToken();
      const bearerString = `Bearer ${token}`;
      headers.set("Authorization", bearerString);

      return headers;
    } catch {
      Log.warn(
        "Be advised, fetching id token failed, user is probably not logged in. Could not set auth headers."
      );

      return headers;
    }
  }
}

/**
 * TODO: specify this
 */
export class NetworkError extends Error {}
export class ParseError extends Error {
  constructor(reason: string, public innerError?: Error) {
    super(reason);
  }
}
